export const namespaced = true;

import SchoolService from '@/services/SchoolService.js';
import CorporationService from '@/services/CorporationService.js';
import cloneDeep from 'lodash/cloneDeep';
import map from 'lodash/map';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { basicPresent, basicSubFieldPresent, basicProductName } from '@/presenters/fields';

export const state = {
  schools: [],
  school: {},
  editingSchool: null,
};

export const mutations = {
  CLEAR_SCHOOLS(state) {
    state.schools = [];
  },
  SET_SCHOOLS(state, schools) {
    state.schools = schools;
  },
  SET_SCHOOL(state, school) {
    state.school = school;
  },
  SET_EDITING_SCHOOL(state, school) {
    state.editingSchool = school;
  },
  DELETE_EDITING_SCHOOL(state) {
    state.editingSchool = null;
  },
  UPDATE_SCHOOL(state, school) {
    state.school = school;
  },
  ADD_SCHOOL(state, school) {
    state.school = school;
  },
  CHANGE_SCHOOL_PASSWORD() {
    // We don't store Password inside Vuex, so we don't need to do anything here
  },
  FREEZE_SCHOOL() {
    // We only freeze School, so we don't need to do anything here
  },
};

export const actions = {
  setCurrentSchoolUser({ commit }, school) {
    commit('SET_SCHOOL', school);
  },
  fetchSchools({ commit }, { data }) {
    return SchoolService.all(data).then((response) => {
      commit('SET_SCHOOLS', response.data);
    });
  },
  fetchSchoolsOfCorporation({ commit }, id) {
    return CorporationService.getSchools(id).then((response) => {
      commit('SET_SCHOOLS', response.data);
    });
  },
  clearSchools({ commit }) {
    commit('CLEAR_SCHOOLS');
  },
  fetchSchool({ commit }, id) {
    return SchoolService.get(id).then((response) => {
      commit('SET_SCHOOL', response.data);
      return response.data;
    });
  },
  startEditingSchool({ commit }, school) {
    commit('SET_EDITING_SCHOOL', school);
  },
  finishEditingSchool({ commit, state }, id) {
    if (state.editingSchool) {
      let formattedSchool = cloneDeep(state.editingSchool);
      if (formattedSchool.corporation) {
        formattedSchool.corporationId = formattedSchool.corporation.id;
        formattedSchool = omit(formattedSchool, ['corporation']);
      }
      if (formattedSchool.brand) {
        formattedSchool.brandId = formattedSchool.brand.id;
        formattedSchool = omit(formattedSchool, ['brand']);
      }
      if (id) {
        return SchoolService.update(id, formattedSchool).then((response) => {
          commit('DELETE_EDITING_SCHOOL');
          commit('UPDATE_SCHOOL', response.data);
        });
      } else {
        return SchoolService.create(formattedSchool).then((response) => {
          commit('DELETE_EDITING_SCHOOL');
          commit('ADD_SCHOOL', response.data);
        });
      }
    }
  },
  clearEditingSchool({ commit, state }) {
    if (state.editingSchool) {
      commit('DELETE_EDITING_SCHOOL');
    }
  },
  changeSchoolPassword({ commit }, { id, password }) {
    return SchoolService.changePassword(id, password).then(() => {
      commit('CHANGE_SCHOOL_PASSWORD');
    });
  },
  freezeSchool({ commit }, payload) {
    let formattedFrozenData = {};
    if (payload.frozenMonth != 'now') {
      formattedFrozenData['frozenDatetime'] = payload.frozenMonth;
    }
    return SchoolService.frozen(payload.id, formattedFrozenData).then(() => {
      commit('FREEZE_SCHOOL');
    });
  },
};

export const getters = {
  getSchoolById: (state) => (id) => {
    return state.schools.find((item) => item.id == id);
  },
  totalSchools: (state) => {
    return state.schools.length;
  },
  getExcelFormattedSchools: (state) => (fields) => {
    return map(state.schools, (school) => {
      let data = {
        法人名: basicSubFieldPresent(school, 'corporation', 'name'),
        ブランド名: basicSubFieldPresent(school, 'brand', 'name'),
        教場ID: basicPresent(school.uid),
        教場名: basicPresent(school.name),
        映像視聴ID数: basicPresent(school.numOfVideos).toString(),
        クラス数: basicPresent(school.numOfClasses).toString(),
        利用教材: basicProductName(school.figuredProducts),
      };

      return pick(data, fields);
    });
  },
  existsRegistrationRequiredParams: (state) => {
    if (!state.editingSchool.uid) return false;
    if (!state.editingSchool.password) return false;
    if (!state.editingSchool.name) return false;
    if (!state.editingSchool.nameReading) return false;
    if (!state.editingSchool.postalCode) return false;
    if (!state.editingSchool.state) return false;
    if (!state.editingSchool.address) return false;
    if (!state.editingSchool.phone) return false;
    if (!state.editingSchool.figuredProducts.length > 0) return false;

    return true;
  },
  existsUpdateRequiredParams: (state) => {
    if (!state.editingSchool.name) return false;
    if (!state.editingSchool.nameReading) return false;
    if (!state.editingSchool.postalCode) return false;
    if (!state.editingSchool.state) return false;
    if (!state.editingSchool.address) return false;
    if (!state.editingSchool.phone) return false;
    if (!state.editingSchool.figuredProducts.length > 0) return false;

    return true;
  },
};
